import React, {useState, useContext, useMemo, useCallback} from 'react';
import OdxAreaHeader from '../../../OdxComponents/NativeOdx/Odx-AreaHeader';
import OdxTable, { TableHeaderCell } from '../../../OdxComponents/NativeOdx/Table/Odx-Table';
import { OdxTableRow } from '../../../OdxComponents/NativeOdx/Table/Odx-TableRow';
import { OdxTableCell } from '../../../OdxComponents/NativeOdx/Table/Odx-Tablecell';
import { odxIconSets } from '../../../OdxComponents/constants';
import RepoSidesheet from "./Components/RepoSidesheet";
import { IRepo } from "../Interfaces/IGithub";
import { generateConsistentColor, getTextColor, PillStyle } from "./Components/ColorUtils";
import OdxBreadcrumbs from "../../../OdxComponents/NativeOdx/Odx-Breadcrumbs";
import { GithubDataContext } from "../githubDataContext";
import { badgeStyle } from "./Styles/styles";
import OdxSearchbar from "../../../OdxComponents/NativeOdx/Odx-Searchbar";
import OdxMainFilterGroup from "../../../OdxComponents/NativeOdx/Odx-MainFilterGroup";
import {formatDate, simplifyForHtmlId} from "../../../Utils/transformer";
import {useModuleViewTracking} from "../../../Utils/sentryTracking";

const HEADERS: TableHeaderCell[] = [
    { name: 'name', title: 'Name', sortable: true, required: true },
    { name: 'updated', title: 'Last updated', sortable: true },
    { name: 'visibility', title: 'Visibility', sortable: false }, // Added column
    { name: 'secrets', title: 'Linked Secrets', sortable: false },
    { name: 'link', title: 'Link', sortable: false }
];

const Overview: React.FC = () => {
    const { repos, secrets, teams, refreshData, org, slug, costCenter, loading } = useContext(GithubDataContext);
    const [openRepo, setOpenRepo] = useState<IRepo | null>(null);
    const [sideOpen, setSideOpen] = useState(false);
    const [searchText, setSearchText] = useState('');
    const baseId = 'github-overview';

    useModuleViewTracking('github-repos')

    const getLinkedSecrets = useCallback((repoId: string | number) => {
        return secrets.filter(
            s => s.selectedRepos?.some(r => r.id.toString() === repoId.toString())
        ).map(s => s.name);
    }, [secrets]);

    const openEdit = useCallback((repo: IRepo) => { setOpenRepo(repo); setSideOpen(true); }, []);
    const openAdd = useCallback(() => { setOpenRepo(null); setSideOpen(true); }, []);

    const filteredRepos = useMemo(() => {
        if (!searchText.trim()) return repos;
        return repos.filter(repo =>
            repo.name.toLowerCase().includes(searchText.toLowerCase())
        );
    }, [repos, searchText]);

    const repoRows = useMemo(() => filteredRepos.map((r) => {
        const linkedSecrets = getLinkedSecrets(r.id);
        const repoUrl = `https://draeger.ghe.com/${org}/${r.name}`;
        const repoKey = simplifyForHtmlId(r.name);
        return (
            <OdxTableRow
                key={r.id}
                id={`${baseId}-row-${repoKey}`}
                onClick={() => openEdit(r)}
                style={{ cursor: 'pointer' }}
            >
                <OdxTableCell className={"cursor-pointer"} id={`${baseId}-row-${repoKey}-name`}>
                    <div className="flex items-center">
                        <div
                            className="w-[5px] h-full !mr-2 min-h-[24px]"
                            style={{
                                backgroundColor: generateConsistentColor(r.name)
                            }}
                        />
                        {r.name}
                    </div>
                </OdxTableCell>
                <OdxTableCell className={"cursor-pointer"} id={`${baseId}-row-${repoKey}-updated`}>{formatDate(r.updated_at)}</OdxTableCell>
                <OdxTableCell className={"cursor-pointer"} id={`${baseId}-row-${repoKey}-visibility`}>
                    {r.private === true ? "Private" : "Internal"}
                </OdxTableCell>
                <OdxTableCell className={"cursor-pointer"} id={`${baseId}-row-${repoKey}-secrets`}>
                    {linkedSecrets.length === 0 ?
                        <span id={`${baseId}-row-${repoKey}-secrets-empty`}>No linked secrets</span>
                        : (
                            <div id={`${baseId}-row-${repoKey}-secrets-list`} style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                                {linkedSecrets.slice(0, 3).map(sn => (
                                    <div key={sn} id={`${baseId}-row-${repoKey}-secret-${simplifyForHtmlId(sn)}`}
                                         style={{ ...PillStyle, backgroundColor: generateConsistentColor(sn), color: getTextColor(generateConsistentColor(sn)) }}>
                                        {sn}
                                    </div>
                                ))}
                                {linkedSecrets.length > 3 && (
                                    <div
                                        id={`${baseId}-row-${repoKey}-secrets-more`}
                                        style={{ alignSelf: 'center', fontWeight: 500, marginLeft: 4 }}
                                    >
                                        +{linkedSecrets.length - 3} more
                                    </div>
                                )}
                            </div>
                        )}
                </OdxTableCell>
                <OdxTableCell className={"cursor-pointer"} id={`${baseId}-row-${repoKey}-link`}>
                    <a id={`${baseId}-row-${repoKey}-link-anchor`} href={repoUrl} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()}>Open Repo</a>
                </OdxTableCell>
            </OdxTableRow>
        );
    }), [filteredRepos, getLinkedSecrets, openEdit, org]);

    return (
        <div id={`${baseId}-view`}>
            <OdxBreadcrumbs id={`${baseId}-breadcrumbs`}/>
            <OdxAreaHeader
                id={`${baseId}-header`}
                title="GitHub Repositories"
                text="GitHub Repositories"
                className={'!pl-0 !pr-0'}
            />
            <OdxMainFilterGroup id={`${baseId}-filters`}>
                <div className="flex-grow" id={`${baseId}-search-container`}>
                    <OdxSearchbar
                        onSearch={setSearchText}
                        id={`${baseId}-search`}
                    />
                </div>
                <div id={`${baseId}-count`}>
                    <span id={`${baseId}-count-label`}>GitHub repositories</span>
                    <span id={`${baseId}-count-badge`} style={badgeStyle}>{filteredRepos.length}</span>
                    <odx-button
                        variant="primary"
                        size="lg"
                        id={`${baseId}-add-btn`}
                        className="!mt-0 !mb-0 !ml-8"
                        type="button"
                        onClick={openAdd}
                    >
                        <odx-icon slot="prefix" size="md" name={`${odxIconSets.core}::plus`}></odx-icon>
                        Add
                    </odx-button>
                </div>
            </OdxMainFilterGroup>
            <div className={"!pt-5 overflow-x-auto"} id={`${baseId}-table-wrapper`}>
                <OdxTable headerData={HEADERS} className={"w-full"} id={`${baseId}-table`}>
                    {filteredRepos.length === 0
                        ? (<OdxTableRow id={`${baseId}-empty-row`}><OdxTableCell colSpan={HEADERS.length}
                                                                                style={{textAlign: 'center'}}
                                                                                id={`${baseId}-empty-cell`}>No
                            repositories found.</OdxTableCell></OdxTableRow>)
                        : repoRows}
                </OdxTable>
            </div>
            <RepoSidesheet
                repo={openRepo}
                costCenter={costCenter}
                isOpen={sideOpen}
                onClose={() => setSideOpen(false)}
                onSaved={refreshData}
                org={org}
                slug={slug}
                allSecrets={secrets}
                allTeams={teams}
                id={`${baseId}-sidesheet`}
            />
        </div>
    );
};

export default Overview;
